/**
 * Liquid-glass style helpers for the drill canvas components.
 *
 * Web2 ships a centralised glass token set in `tokens.css`
 * (`--glass-bg`, `--glass-border`, `--glass-blur`, `--glass-shadow`,
 * `--glass-glow-edge`). These helpers expose the same tokens as
 * React style objects so the drill-canvas components don't drift into
 * hand-rolled rgba values.
 *
 * Three tiers:
 *   - SURFACE  — pill / button / chip-sized translucent surface
 *   - PANEL    — card / drawer-sized; richer shadow + edge highlight
 *   - MODAL    — sheet / overlay; full backdrop blur + the floating
 *                edge-glow that gives liquid glass its specular shine
 */

import type { CSSProperties } from 'react';

const FILTER = 'blur(var(--glass-blur)) saturate(var(--glass-saturate)) brightness(var(--glass-brightness))';

/** Small floating chip / tray / pill. */
export const glassSurface: CSSProperties = {
  background: 'var(--glass-bg)',
  borderStyle: 'solid',
  borderWidth: 1,
  borderColor: 'var(--glass-border)',
  borderRadius: 10,
  color: '#fff',
  backdropFilter: FILTER,
  WebkitBackdropFilter: FILTER,
  boxShadow: 'var(--glass-shadow)',
};

/** Larger drawer / card surface — adds the edge-glow gradient overlay. */
export const glassPanel: CSSProperties = {
  background: 'var(--glass-bg)',
  borderStyle: 'solid',
  borderWidth: 1,
  borderColor: 'var(--glass-border)',
  borderRadius: 12,
  color: '#fff',
  backdropFilter: FILTER,
  WebkitBackdropFilter: FILTER,
  boxShadow: 'var(--glass-shadow)',
  // Specular highlight at the top edge — what gives liquid glass its
  // shine. Layered as a backgroundImage so it sits over the bg colour.
  backgroundImage: 'var(--glass-glow-edge)',
  backgroundBlendMode: 'overlay',
};

/** Sheet / modal — same as panel but heavier shadow + stronger border. */
export const glassModal: CSSProperties = {
  ...glassPanel,
  borderColor: 'var(--glass-border-strong)',
  boxShadow:
    '0 32px 100px -20px rgba(0, 0, 0, 0.65), 0 1px 0 rgba(255, 255, 255, 0.10) inset',
};

/** Backdrop behind a modal — dims everything else + adds a faint blur. */
export const glassBackdrop: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0, 0, 0, 0.45)',
  backdropFilter: 'blur(8px) saturate(140%)',
  WebkitBackdropFilter: 'blur(8px) saturate(140%)',
  zIndex: 1000,
};

/** Default glass button (outlined, translucent fill). */
export const glassButton: CSSProperties = {
  padding: '6px 14px',
  background: 'var(--glass-bg-hover)',
  color: '#fff',
  borderStyle: 'solid',
  borderWidth: 1,
  borderColor: 'var(--glass-border)',
  borderRadius: 999,
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
  backdropFilter: FILTER,
  WebkitBackdropFilter: FILTER,
  boxShadow: '0 1px 0 rgba(255,255,255,0.08) inset, 0 2px 8px -2px rgba(0,0,0,0.3)',
  transition: 'background 150ms, border-color 150ms, transform 80ms',
};

/** Active / primary glass button (accent fill). */
export const glassButtonActive: CSSProperties = {
  ...glassButton,
  background: 'var(--glass-bg-active)',
  borderColor: 'var(--glass-border-strong)',
  color: '#fff',
};

/** Subtle input field with the same glass treatment. */
export const glassInput: CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  color: '#fff',
  borderStyle: 'solid',
  borderWidth: 1,
  borderColor: 'var(--glass-border)',
  borderRadius: 8,
  padding: '7px 10px',
  fontSize: 13,
  width: '100%',
  boxSizing: 'border-box',
  backdropFilter: 'blur(12px) saturate(150%)',
  WebkitBackdropFilter: 'blur(12px) saturate(150%)',
};
